import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Shell, BottomNav } from "@/components/Shell";
import { BottomSheet } from "@/components/BottomSheet";
import { ChaseSheet } from "@/components/ChaseSheet";
import { money, computeTotals, DOC_META } from "@/lib/format";
import { Sparkles, Send, Lock, Check, FileText, Mic, Square, Loader2, X, MessageSquare, CheckCircle2, Plus, Trash2, Pencil } from "lucide-react";

const KaiReviewSheet = ({ acts, onClose, onConfirm, creating }) => {
  const [items, setItems] = useState(() => acts.map((a) => JSON.parse(JSON.stringify(a))));
  const setDoc = (i, fn) => setItems((prev) => prev.map((a, idx) => (idx === i ? fn(a) : a)));
  const d = (a) => a.payload?.data || {};
  const setField = (i, k, v) => setDoc(i, (a) => ({ ...a, payload: { ...a.payload, data: { ...d(a), [k]: v } } }));
  const setClient = (i, v) => setDoc(i, (a) => ({ ...a, payload: { ...a.payload, client: { ...(a.payload?.client || {}), name: v } } }));
  const setLine = (i, li, k, v) => setDoc(i, (a) => { const l = [...(d(a).line_items || [])]; l[li] = { ...l[li], [k]: v }; return { ...a, payload: { ...a.payload, data: { ...d(a), line_items: l } } }; });
  const addLine = (i) => setDoc(i, (a) => ({ ...a, payload: { ...a.payload, data: { ...d(a), line_items: [...(d(a).line_items || []), { description: "", qty: "1", unit_price: "" }] } } }));
  const delLine = (i, li) => setDoc(i, (a) => ({ ...a, payload: { ...a.payload, data: { ...d(a), line_items: (d(a).line_items || []).filter((_, x) => x !== li) } } }));

  return (
    <BottomSheet open onClose={onClose} title={acts.length > 1 ? `Review ${acts.length} drafts` : "Review draft"} testid="kai-review-sheet">
      <div className="space-y-4 max-h-[64vh] overflow-y-auto no-scrollbar pr-0.5">
        {items.map((a, i) => {
          const data = d(a); const meta = DOC_META[a.doc_type] || {};
          const isWaste = a.doc_type === "waste_note";
          return (
            <div key={i} data-testid={`kai-review-doc-${i}`} className="border border-border rounded-[14px] p-3.5">
              <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full" style={{ color: meta.color, background: (meta.color || "#999") + "1a" }}>{meta.label}</span>
              <input data-testid={`kai-review-client-${i}`} value={a.payload?.client?.name || ""} onChange={(e) => setClient(i, e.target.value)} placeholder="Client name" className="w-full mt-2 bg-surfaceSecondary border border-border rounded-[10px] px-3 py-2 text-base focus:outline-none focus:border-brandPrimary" />
              <input data-testid={`kai-review-title-${i}`} value={data.title || ""} onChange={(e) => setField(i, "title", e.target.value)} placeholder="Title / job" className="w-full mt-2 bg-surfaceSecondary border border-border rounded-[10px] px-3 py-2 text-base focus:outline-none focus:border-brandPrimary" />
              {isWaste ? (
                <textarea value={data.description || ""} onChange={(e) => setField(i, "description", e.target.value)} placeholder="Waste description" rows={2} className="w-full mt-2 bg-surfaceSecondary border border-border rounded-[10px] px-3 py-2 text-base focus:outline-none focus:border-brandPrimary resize-none" />
              ) : (
                <div className="mt-2 space-y-1.5">
                  {(data.line_items || []).map((li, x) => (
                    <div key={x} className="flex items-center gap-1.5">
                      <input value={li.description} onChange={(e) => setLine(i, x, "description", e.target.value)} placeholder="Item" className="flex-1 min-w-0 bg-surfaceSecondary rounded-[8px] px-2 py-1.5 text-base focus:outline-none" />
                      <input value={li.qty} onChange={(e) => setLine(i, x, "qty", e.target.value.replace(/[^0-9.]/g, ""))} inputMode="decimal" className="w-10 bg-surfaceSecondary rounded-[8px] px-1.5 py-1.5 text-base text-center focus:outline-none" />
                      <input value={li.unit_price} onChange={(e) => setLine(i, x, "unit_price", e.target.value.replace(/[^0-9.]/g, ""))} inputMode="decimal" placeholder="£" className="w-16 bg-surfaceSecondary rounded-[8px] px-2 py-1.5 text-base text-right focus:outline-none" />
                      <button onClick={() => delLine(i, x)} className="text-error p-1"><Trash2 size={15} /></button>
                    </div>
                  ))}
                  <button data-testid={`kai-review-addline-${i}`} onClick={() => addLine(i)} className="text-brandPrimary text-xs font-bold flex items-center gap-1 mt-1"><Plus size={13} /> Add line</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button data-testid="kai-review-confirm" disabled={creating} onClick={() => onConfirm(items)} className="w-full mt-4 bg-brandPrimary text-onBrand rounded-full h-12 font-heading font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50">
        <Check size={17} /> {creating ? "Creating…" : `Confirm & create ${acts.length > 1 ? acts.length + " docs" : "document"}`}
      </button>
    </BottomSheet>
  );
};

const SESSION_KEY = "kai_session";
const getSession = () => { try { return localStorage.getItem(SESSION_KEY) || "main"; } catch { return "main"; } };

const DocSummary = ({ action }) => {
  const p = action.payload || {};
  const data = p.data || {};
  const items = data.line_items || [];
  const t = computeTotals(items.map((i) => ({ qty: Number(i.qty), unit_price: Number(i.unit_price) })), data.tax_rate || 0);
  const meta = DOC_META[action.doc_type] || {};
  return (
    <div className="border-t border-border pt-2.5 mt-2.5 first:border-0 first:pt-0 first:mt-0">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full" style={{ color: meta.color, background: (meta.color || "#999") + "1a" }}>{meta.label}</span>
        {p.client?.name && <span className="text-xs font-semibold text-onSurface">{p.client.name}</span>}
      </div>
      {data.title && <p className="font-heading font-bold text-sm text-onSurface">{data.title}</p>}
      {items.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {items.map((li, i) => <div key={i} className="flex justify-between text-sm"><span className="text-onSurface flex-1 pr-2">{li.description} × {li.qty}</span><span className="font-semibold">{money(Number(li.qty) * Number(li.unit_price))}</span></div>)}
          <div className="flex justify-between font-heading font-bold text-sm pt-1 border-t border-border mt-0.5"><span>Total</span><span className="text-brandPrimary">{money(t.total)}</span></div>
        </div>
      )}
      {action.doc_type === "waste_note" && data.description && <p className="text-sm text-onSurface mt-0.5">{data.description}</p>}
    </div>
  );
};

const MultiConfirm = ({ actions, onConfirm, onView, creating }) => {
  const multi = actions.length > 1;
  const meta = DOC_META[actions[0]?.doc_type] || {};
  const label = multi ? `${actions.length} documents` : (meta.label || "document").toLowerCase();
  return (
    <div data-testid="kai-confirm-card" className="bg-surface border-2 border-brandPrimary rounded-[16px] p-4 mt-2 self-start max-w-[92%] w-full shadow-md animate-fade-up">
      <p className="text-xs font-semibold text-muted-foreground mb-1">{multi ? `${actions.length} drafts ready` : "Draft ready"}</p>
      {actions.map((a, i) => <DocSummary key={i} action={a} />)}
      <button data-testid="kai-view-btn" onClick={onView} className="w-full mt-3 bg-surfaceSecondary border border-border text-onSurface rounded-full h-11 font-heading font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
        <Pencil size={15} /> View & edit {multi ? "documents" : "before saving"}
      </button>
      <button data-testid="kai-confirm-btn" disabled={creating} onClick={onConfirm} className="w-full mt-2 bg-brandPrimary text-onBrand rounded-full h-11 font-heading font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50">
        <Check size={16} /> {creating ? "Creating…" : `Confirm & create ${label}`}
      </button>
    </div>
  );
};

const ChaseActionCard = ({ action, onChase }) => {
  const p = action.payload || {};
  return (
    <div data-testid="kai-chase-card" className="bg-surface border-2 border-brandPrimary rounded-[16px] p-4 mt-2 self-start max-w-[92%] w-full shadow-md animate-fade-up">
      <p className="text-xs font-semibold text-muted-foreground mb-0.5">Follow-up ready to review</p>
      <p className="font-heading font-bold text-sm text-onSurface">{action.summary || `Chase ${p.client || ""} ${p.number || ""}`}</p>
      <button data-testid="kai-chase-open" onClick={() => onChase(p.doc_id)} className="w-full mt-3 bg-brandPrimary text-onBrand rounded-full h-11 font-heading font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
        <MessageSquare size={16} /> Review & Send
      </button>
    </div>
  );
};

const MarkPaidCard = ({ action, paid, onPaid }) => {
  const p = action.payload || {};
  return (
    <div data-testid="kai-paid-card" className="bg-surface border-2 border-success/40 rounded-[16px] p-4 mt-2 self-start max-w-[92%] w-full shadow-md animate-fade-up">
      <p className="text-xs font-semibold text-muted-foreground mb-0.5">Confirm payment</p>
      <p className="font-heading font-bold text-sm text-onSurface">{action.summary || `Mark ${p.number || ""} as paid`}</p>
      <button data-testid="kai-paid-confirm" disabled={paid} onClick={() => onPaid(p.doc_id)} className="w-full mt-3 bg-success text-white rounded-full h-11 font-heading font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
        <CheckCircle2 size={16} /> {paid ? "Marked paid ✓" : "Yes, mark as paid"}
      </button>
    </div>
  );
};

export default function Kai() {
  const navigate = useNavigate();
  const location = useLocation();
  const { access, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const scroller = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const recognitionRef = useRef(null);
  const finalRef = useRef("");
  const [chaseId, setChaseId] = useState(null);
  const [paidIds, setPaidIds] = useState(() => new Set());
  const [reviewActs, setReviewActs] = useState(null);
  const [session, setSession] = useState(getSession);

  const newChat = () => {
    if (sending || creating) return;
    const s = "s-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    try { localStorage.setItem(SESSION_KEY, s); } catch {}
    setSession(s);
    setMessages([]);
    setInput("");
    setReviewActs(null);
    setChaseId(null);
    setPaidIds(new Set());
  };

  const markPaid = async (docId) => {
    try { await api.patch(`/documents/${docId}`, { status: "paid" }); setPaidIds((s) => new Set(s).add(docId)); toast.success("Marked as paid"); }
    catch { toast.error("Couldn't update"); }
  };

  const locked = access && !access.can_use_kai;

  useEffect(() => { if (!locked) api.get("/kai/history", { params: { session_id: session } }).then(({ data }) => setMessages(data.messages)); }, [locked, session]);
  useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" }); }, [messages, sending, transcribing]);

  const sendText = async (raw) => {
    const text = (raw || "").trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { id: "tmp-" + Date.now(), role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/kai/chat", { message: text, session_id: session });
      setMessages((m) => [...m, { id: data.message.id, role: "assistant", content: data.reply, action: data.action, actions: data.actions }]);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Unlock Kai to chat");
      else toast.error("Kai couldn't respond");
      setMessages((m) => m.slice(0, -1));
      setInput(text);
    } finally { setSending(false); }
  };

  const send = () => sendText(input);

  const startRec = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      // Live, on-device transcription — words appear as you speak, no upload lag
      try {
        const rec = new SR();
        rec.lang = "en-GB";
        rec.interimResults = true;
        rec.continuous = true;
        finalRef.current = "";
        rec.onresult = (e) => {
          let interim = "";
          for (let i = e.resultIndex; i < e.results.length; i++) {
            const t = e.results[i][0].transcript;
            if (e.results[i].isFinal) finalRef.current += t + " ";
            else interim += t;
          }
          setInput((finalRef.current + interim).trim());
        };
        rec.onerror = (ev) => {
          setRecording(false);
          if (ev.error === "not-allowed" || ev.error === "service-not-allowed") toast.error("Microphone blocked — allow mic access");
          else if (ev.error === "no-speech") toast.error("Didn't hear anything — try again");
        };
        rec.onend = () => {
          setRecording(false);
          const text = finalRef.current.trim();
          if (text) { setInput(""); sendText(text); }
        };
        recognitionRef.current = rec;
        rec.start();
        setRecording(true);
        return;
      } catch (e) { /* fall back to Whisper below */ }
    }
    // Fallback: record + Whisper transcription
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice isn't supported on this browser"); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mime = "";
      for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"]) {
        if (MediaRecorder.isTypeSupported(m)) { mime = m; break; }
      }
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = (mr.mimeType || "audio/webm").split(";")[0];
        const ext = type.includes("mp4") ? "mp4" : type.includes("ogg") ? "ogg" : "webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size < 400) { toast.error("Didn't catch that — hold a little longer"); return; }
        setTranscribing(true);
        const fd = new FormData();
        fd.append("file", blob, `audio.${ext}`);
        try {
          const { data } = await api.post("/kai/transcribe", fd);
          if (data.text && data.text.trim()) await sendText(data.text);
          else toast.error("Didn't catch that — try again");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Couldn't transcribe — try again");
        } finally { setTranscribing(false); }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone blocked — allow mic access in your browser");
    }
  };

  const stopRec = () => {
    if (recognitionRef.current && recording) { try { recognitionRef.current.stop(); } catch (e) {} return; }
    if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); }
  };
  const toggleRec = () => (recording ? stopRec() : startRec());

  // Auto-start listening when arriving from the dashboard "Voice the job" button.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (locked || autoStartedRef.current) return;
    if (location.state?.voice) {
      autoStartedRef.current = true;
      navigate(location.pathname, { replace: true, state: {} }); // clear so it won't retrigger
      const t = setTimeout(() => { if (!recording) startRec(); }, 400);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [locked, location.state]);

  const confirmCreateAll = async (actions) => {
    setCreating(true);
    const ids = [];
    try {
      for (const action of actions) {
        const p = action.payload || {};
        const data = { ...(p.data || {}) };
        if (action.doc_type !== "waste_note") {
          const items = (data.line_items || []).map((i) => ({ description: i.description, qty: Number(i.qty || 0), unit_price: Number(i.unit_price || 0) }));
          Object.assign(data, { line_items: items, tax_rate: Number(data.tax_rate || 0), ...computeTotals(items, data.tax_rate || 0) });
        }
        const { data: res } = await api.post("/documents", { type: action.doc_type, data, client: p.client?.name ? { name: p.client.name } : null });
        ids.push(res.document.id);
      }
      if (ids.length === 1) { toast.success("Document created"); navigate(`/doc/${ids[0]}`); }
      else { toast.success(`${ids.length} documents created`); navigate("/documents"); }
    } catch (e) {
      if (e?.response?.status === 402 || e?.response?.status === 403) navigate("/paywall");
      else toast.error("Could not create all documents");
    } finally { setCreating(false); setReviewActs(null); }
  };

  if (locked) {
    return (
      <Shell showNav title="Kai">
        <div className="flex flex-col items-center text-center pt-10 animate-fade-up">
          <div className="w-20 h-20 rounded-[24px] bg-brandTertiary flex items-center justify-center mb-5 relative">
            <Sparkles size={36} className="text-brandPrimary" />
            <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-onSurface text-onBrand flex items-center justify-center"><Lock size={15} /></div>
          </div>
          <h2 className="font-heading text-2xl font-extrabold text-onSurface">Meet Kai</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-xs">Describe a job in plain English and Kai drafts the quote, invoice or waste note for you — you just tap confirm.</p>
          <div className="bg-surface border border-border rounded-[16px] p-4 mt-6 w-full text-left space-y-2">
            <p className="text-sm text-onSurface">"Quote for a full bathroom refit — labour £1200, tiles £300, new suite £650"</p>
          </div>
          <button data-testid="kai-unlock-btn" onClick={() => navigate("/paywall")} className="w-full mt-6 bg-brandPrimary text-onBrand rounded-full h-13 py-3.5 font-heading font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
            <Sparkles size={18} /> Subscribe — £12/mo
          </button>
          <p className="text-xs text-muted-foreground mt-2">Kai is included with your subscription.</p>
        </div>
      </Shell>
    );
  }

  return (
    <div className="min-h-screen bg-surfaceSecondary flex justify-center">
      <div className="w-full max-w-md bg-surfaceSecondary flex flex-col" style={{ height: "100dvh" }}>
        <header className="sticky top-0 z-40 bg-surface/90 backdrop-blur-md border-b border-border px-4 h-16 flex items-center gap-3">
          <div className="w-9 h-9 rounded-[10px] bg-brandTertiary flex items-center justify-center"><Sparkles size={20} className="text-brandPrimary" /></div>
          <div className="flex-1"><h1 className="font-heading text-lg font-bold text-onSurface">Kai</h1><p className="text-xs text-success flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-success inline-block" /> Ready to help</p></div>
          <button data-testid="kai-new-chat" onClick={newChat} disabled={sending || creating || messages.length === 0} className="flex items-center gap-1 text-sm font-semibold text-muted-foreground px-2.5 py-1.5 rounded-full hover:bg-surfaceTertiary active:scale-95 transition-transform disabled:opacity-40">
            <Plus size={16} /> New
          </button>
          <button data-testid="kai-exit" onClick={() => navigate("/home")} className="flex items-center gap-1 text-sm font-semibold text-muted-foreground px-2.5 py-1.5 rounded-full hover:bg-surfaceTertiary active:scale-95 transition-transform">
            <X size={16} /> Exit
          </button>
        </header>

        <div ref={scroller} className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 flex flex-col gap-3" style={{ paddingBottom: 12 }}>
          {messages.length === 0 && (
            <div className="text-center pt-8 animate-fade-up">
              <div className="w-14 h-14 rounded-[16px] bg-brandTertiary flex items-center justify-center mx-auto mb-3"><Sparkles size={26} className="text-brandPrimary" /></div>
              <p className="font-heading font-bold text-onSurface">Hi {user?.name?.split(" ")[0] || "there"} 👋</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">Tap the mic and just talk, or type. I can even draft several documents at once. Try one:</p>
              <div className="flex flex-col gap-2 mt-4">
                {["Quote for rewiring a 2-bed flat, £1800 labour + £400 materials", "Invoice Dave £90 for a boiler service and a tap swap £120"].map((s) => (
                  <button key={s} data-testid="kai-suggestion" onClick={() => setInput(s)} className="text-left text-sm bg-surface border border-border rounded-[12px] p-3 hover:border-brandPrimary transition-colors">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => {
            const acts = m.actions?.length ? m.actions : (m.action ? [m.action] : []);
            const createActs = acts.filter((a) => a && (!a.type || a.type === "create_document"));
            const chaseActs = acts.filter((a) => a && a.type === "chase");
            const paidActs = acts.filter((a) => a && a.type === "mark_paid");
            return (
              <React.Fragment key={m.id}>
                <div className={`p-3 max-w-[85%] text-sm leading-relaxed shadow-sm ${m.role === "user" ? "bg-brandPrimary text-onBrand rounded-[18px] rounded-tr-sm self-end" : "bg-brandTertiary text-onSurface rounded-[18px] rounded-tl-sm self-start"}`}>
                  {m.content}
                </div>
                {m.role === "assistant" && createActs.length > 0 && <MultiConfirm actions={createActs} onConfirm={() => confirmCreateAll(createActs)} onView={() => setReviewActs(createActs)} creating={creating} />}
                {m.role === "assistant" && chaseActs.map((a, i) => <ChaseActionCard key={`c${i}`} action={a} onChase={setChaseId} />)}
                {m.role === "assistant" && paidActs.map((a, i) => <MarkPaidCard key={`p${i}`} action={a} paid={paidIds.has(a.payload?.doc_id)} onPaid={markPaid} />)}
              </React.Fragment>
            );
          })}
          {sending && <div className="bg-brandTertiary rounded-[18px] rounded-tl-sm self-start p-3 flex gap-1"><Dot /><Dot d={150} /><Dot d={300} /></div>}
        </div>

        {recording && (
          <div className="px-4 pb-2">
            <div className="flex items-center gap-3 bg-error/10 border border-error/30 rounded-[16px] px-4 py-3">
              <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-error opacity-60" /><span className="relative inline-flex rounded-full h-3 w-3 bg-error" /></span>
              <span className="text-sm font-semibold text-onSurface flex-1">Listening… tap to finish</span>
              <VoiceBars />
            </div>
          </div>
        )}
        {transcribing && (
          <div className="px-4 pb-2"><div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={16} className="animate-spin text-brandPrimary" /> Transcribing…</div></div>
        )}

        <div className="bg-surface border-t border-border p-3 flex items-end gap-2">
          <button data-testid="kai-mic" onClick={toggleRec} disabled={transcribing || sending} className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 transition-colors active:scale-90 disabled:opacity-40 ${recording ? "bg-error text-white" : "bg-surfaceTertiary text-onSurface"}`} aria-label={recording ? "Stop recording" : "Start recording"}>
            {recording ? <Square size={18} fill="currentColor" /> : <Mic size={20} />}
          </button>
          <textarea data-testid="kai-input" rows={1} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={recording ? "Listening…" : "Speak or type the job…"} className="flex-1 bg-surfaceSecondary rounded-[20px] px-4 py-2.5 text-base focus:outline-none resize-none max-h-28" />
          <button data-testid="kai-send" onClick={send} disabled={!input.trim() || sending} className="w-11 h-11 rounded-full bg-brandPrimary text-onBrand flex items-center justify-center active:scale-90 transition-transform disabled:opacity-40 shrink-0"><Send size={18} /></button>
        </div>

        <BottomNav floating={false} />
      </div>
      {chaseId && <ChaseSheet docId={chaseId} onClose={() => setChaseId(null)} />}
      {reviewActs && <KaiReviewSheet acts={reviewActs} creating={creating} onClose={() => setReviewActs(null)} onConfirm={(edited) => confirmCreateAll(edited)} />}
    </div>
  );
}

const Dot = ({ d = 0 }) => <span className="w-2 h-2 rounded-full bg-brandSecondary/60 animate-bounce" style={{ animationDelay: `${d}ms` }} />;

const VoiceBars = () => (
  <div className="flex items-end gap-0.5 h-5">
    {[0, 120, 240, 360, 180].map((d, i) => (
      <span key={i} className="w-1 bg-error rounded-full animate-bounce" style={{ height: `${8 + (i % 3) * 6}px`, animationDelay: `${d}ms`, animationDuration: "0.6s" }} />
    ))}
  </div>
);
